import { defineStore } from 'pinia'
import { persistConfig } from '../index'

/**
 * UserPreferences Store
 * 管理用户个性化的数据和偏好
 */
export const useUserPreferencesStore = defineStore('userPreferences', {
  state: () => ({
    // 最近访问的页面记录
    recentPages: [],
    
    // 收藏的页面/项目
    favorites: [],
    
    // 常用功能快捷方式
    quickAccess: [],
    
    // 布局偏好
    layouts: {
      // 默认视图布局
      defaultView: 'list',
      // 列表视图配置
      listView: {
        columns: [],
        sortBy: 'id',
        sortOrder: 'asc',
        pageSize: 20
      },
      // 网格视图配置
      gridView: {
        columns: 3,
        showLabels: true
      }
    },
    
    // 搜索历史
    searchHistory: [],
    
    // 过滤条件记忆
    filterPresets: {},
    
    // 自定义字段
    customFields: [],
    
    // 使用统计
    usageStats: {
      // 功能使用次数
      featureUsage: {},
      // 应用启动次数
      appLaunches: 0,
      // 首次使用时间
      firstUsed: null,
      // 上次使用时间
      lastUsed: null
    }
  }),
  
  getters: {
    // 获取最近访问的页面（限制数量）
    recentPagesLimited: (state) => {
      // 限制显示最近20个页面
      return state.recentPages.slice(0, 20)
    },
    
    // 检查项目是否已收藏
    isFavorite: (state) => (itemId, category) => {
      return state.favorites.some(fav => fav.id === itemId && fav.category === category)
    },
    
    // 获取指定视图的布局配置
    getLayoutConfig: (state) => (viewType) => {
      return state.layouts[viewType] || state.layouts.defaultView
    },
    
    // 获取搜索历史（限制数量）
    recentSearches: (state) => {
      // 限制显示最近10个搜索
      return state.searchHistory.slice(0, 10)
    },
    
    // 获取使用统计概览
    usageOverview: (state) => {
      const { featureUsage, appLaunches, firstUsed, lastUsed } = state.usageStats
      
      // 计算最常用的功能
      const mostUsedFeature = Object.entries(featureUsage)
        .sort((a, b) => b[1] - a[1])[0]
        ?.map(item => item)
        || null
      
      return {
        appLaunches,
        firstUsed,
        lastUsed,
        mostUsedFeature,
        totalFeaturesUsed: Object.keys(featureUsage).length
      }
    }
  },
  
  actions: {
    // 添加访问记录
    addRecentPage(page) {
      const { id, title, path, timestamp = Date.now() } = page
      
      // 移除已存在的相同页面
      this.recentPages = this.recentPages.filter(p => p.id !== id)
      
      // 添加到开头
      this.recentPages.unshift({ id, title, path, timestamp })
      
      // 限制数量
      if (this.recentPages.length > 50) {
        this.recentPages = this.recentPages.slice(0, 50)
      }
    },
    
    // 清空访问记录
    clearRecentPages() {
      this.recentPages = []
    },
    
    // 添加到收藏
    addFavorite(item) {
      const { id, category, title, path, timestamp = Date.now() } = item
      
      // 检查是否已存在
      if (!this.isFavorite(id, category)) {
        this.favorites.push({ id, category, title, path, timestamp })
      }
    },
    
    // 从收藏中移除
    removeFavorite(itemId, category) {
      this.favorites = this.favorites.filter(fav => !(fav.id === itemId && fav.category === category))
    },
    
    // 清空收藏
    clearFavorites() {
      this.favorites = []
    },
    
    // 添加搜索历史
    addSearchHistory(query, category = 'general') {
      // 移除已存在的相同搜索
      this.searchHistory = this.searchHistory.filter(item => !(item.query === query && item.category === category))
      
      // 添加到开头
      this.searchHistory.unshift({ query, category, timestamp: Date.now() })
      
      // 限制数量
      if (this.searchHistory.length > 100) {
        this.searchHistory = this.searchHistory.slice(0, 100)
      }
    },
    
    // 清空搜索历史
    clearSearchHistory(category = null) {
      if (category) {
        this.searchHistory = this.searchHistory.filter(item => item.category !== category)
      } else {
        this.searchHistory = []
      }
    },
    
    // 保存布局配置
    saveLayoutConfig(viewType, config) {
      if (this.layouts[viewType]) {
        this.layouts[viewType] = { ...this.layouts[viewType], ...config }
      }
    },
    
    // 保存过滤预设
    saveFilterPreset(name, filters, category) {
      if (!this.filterPresets[category]) {
        this.filterPresets[category] = {}
      }
      this.filterPresets[category][name] = {
        filters,
        savedAt: Date.now()
      }
    },
    
    // 记录功能使用
    recordFeatureUsage(featureName) {
      if (!this.usageStats.featureUsage[featureName]) {
        this.usageStats.featureUsage[featureName] = 0
      }
      this.usageStats.featureUsage[featureName]++
    },
    
    // 记录应用启动
    recordAppLaunch() {
      this.usageStats.appLaunches++
      this.usageStats.lastUsed = Date.now()
      
      // 首次启动记录
      if (!this.usageStats.firstUsed) {
        this.usageStats.firstUsed = Date.now()
      }
    },
    
    // 添加自定义字段
    addCustomField(field) {
      const { id, name, type, defaultValue, category } = field
      
      // 检查是否已存在
      if (!this.customFields.some(f => f.id === id)) {
        this.customFields.push({
          id,
          name,
          type,
          defaultValue,
          category,
          createdAt: Date.now()
        })
      }
    },
    
    // 移除自定义字段
    removeCustomField(fieldId) {
      this.customFields = this.customFields.filter(field => field.id !== fieldId)
    },
    
    // 重置用户偏好
    resetPreferences() {
      // 保留重要的统计信息
      const usageStats = this.usageStats
      
      this.$reset()
      
      // 恢复重要的统计信息
      this.usageStats = {
        ...this.usageStats,
        appLaunches: usageStats.appLaunches,
        firstUsed: usageStats.firstUsed,
        lastUsed: Date.now()
      }
    }
  },
  
  // 配置持久化
  persist: persistConfig.createPersistConfig('userPreferences', {
    // 持久化所有用户偏好
    paths: ['recentPages', 'favorites', 'quickAccess', 'layouts', 'searchHistory', 'filterPresets', 'customFields', 'usageStats']
  })
})